class SpecialEditionsSlider extends HTMLElement {
  #slides = [];
  #backgrounds = [];
  #dots = [];
  #current = 0;
  #startX = null;
  #startY = null;
  #didSwipe = false;
  #pointerId = null;

  connectedCallback() {
    this.#slides = Array.from(this.querySelectorAll('[data-edition-slide]'));
    this.#backgrounds = Array.from(this.querySelectorAll('[data-edition-background]'));
    this.#dots = Array.from(this.querySelectorAll('[data-edition-dot]'));

    this.querySelector('[data-edition-previous]')?.addEventListener('click', this.#showPrevious);
    this.querySelector('[data-edition-next]')?.addEventListener('click', this.#showNext);
    this.addEventListener('keydown', this.#handleKeydown);

    this.addEventListener('pointerdown', this.#handlePointerDown);
    this.addEventListener('pointermove', this.#handlePointerMove, { passive: true });
    this.addEventListener('pointerup', this.#handlePointerUp);
    this.addEventListener('pointercancel', this.#handlePointerCancel);
    this.addEventListener('touchstart', this.#handleTouchStart, { passive: true });
    this.addEventListener('touchmove', this.#handleTouchMove, { passive: true });
    this.addEventListener('touchend', this.#handleTouchEnd, { passive: true });
    this.addEventListener('click', this.#handleClickCapture, true);
    document.addEventListener('shopify:block:select', this.#handleBlockSelect);

    for (const dot of this.#dots) dot.addEventListener('click', this.#handleDotClick);

    this.#render();
    this.#applyImagePalettes();
  }

  disconnectedCallback() {
    this.querySelector('[data-edition-previous]')?.removeEventListener('click', this.#showPrevious);
    this.querySelector('[data-edition-next]')?.removeEventListener('click', this.#showNext);
    this.removeEventListener('keydown', this.#handleKeydown);
    this.removeEventListener('pointerdown', this.#handlePointerDown);
    this.removeEventListener('pointermove', this.#handlePointerMove);
    this.removeEventListener('pointerup', this.#handlePointerUp);
    this.removeEventListener('pointercancel', this.#handlePointerCancel);
    this.removeEventListener('touchstart', this.#handleTouchStart);
    this.removeEventListener('touchmove', this.#handleTouchMove);
    this.removeEventListener('touchend', this.#handleTouchEnd);
    this.removeEventListener('click', this.#handleClickCapture, true);
    document.removeEventListener('shopify:block:select', this.#handleBlockSelect);

    for (const dot of this.#dots) dot.removeEventListener('click', this.#handleDotClick);
  }

  #showPrevious = () => {
    this.#goTo(this.#current - 1);
  };

  #showNext = () => {
    this.#goTo(this.#current + 1);
  };

  #handleKeydown = (event) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.#showPrevious();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.#showNext();
    }
  };

  #handlePointerDown = (event) => {
    if (event.button !== 0) return;
    if (event.target.closest('[data-edition-previous],[data-edition-next],[data-edition-dot]')) return;
    this.#startX = event.clientX;
    this.#startY = event.clientY;
    this.#didSwipe = false;
    this.#pointerId = event.pointerId;
  };

  #handlePointerMove = (event) => {
    if (this.#startX === null || event.pointerId !== this.#pointerId) return;
  };

  #handlePointerUp = (event) => {
    if (this.#startX === null || event.pointerId !== this.#pointerId) return;
    const dx = event.clientX - this.#startX;
    const dy = event.clientY - this.#startY;
    this.#startX = null;
    this.#startY = null;
    this.#pointerId = null;
    if (Math.abs(dx) < 40) return;
    if (Math.abs(dx) < Math.abs(dy) * 1.2) return;
    this.#didSwipe = true;
    if (dx > 0) this.#showPrevious();
    else this.#showNext();
    setTimeout(() => (this.#didSwipe = false), 600);
  };

  #handlePointerCancel = (event) => {
    if (event.pointerId !== this.#pointerId) return;
    this.#startX = null;
    this.#startY = null;
    this.#pointerId = null;
  };

  #handleTouchStart = (event) => {
    if (event.touches.length !== 1) return;
    if (event.target.closest('[data-edition-previous],[data-edition-next],[data-edition-dot]')) return;
    this.#startX = event.touches[0].clientX;
    this.#startY = event.touches[0].clientY;
    this.#didSwipe = false;
  };

  #handleTouchMove = (event) => {
    if (this.#startX === null) return;
  };

  #handleTouchEnd = (event) => {
    if (this.#startX === null) return;
    const touch = event.changedTouches[0];
    if (!touch) return;
    const dx = touch.clientX - this.#startX;
    const dy = touch.clientY - this.#startY;
    this.#startX = null;
    this.#startY = null;
    if (Math.abs(dx) < 40) return;
    if (Math.abs(dx) < Math.abs(dy) * 1.2) return;
    this.#didSwipe = true;
    if (dx > 0) this.#showPrevious();
    else this.#showNext();
    setTimeout(() => (this.#didSwipe = false), 600);
  };

  #handleClickCapture = (event) => {
    if (!this.#didSwipe) return;
    const link = event.target.closest('a');
    if (link && link.closest('[data-edition-slide]')) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  #handleDotClick = (event) => {
    if (!(event.currentTarget instanceof HTMLButtonElement)) return;
    this.#goTo(Number(event.currentTarget.dataset.editionDot));
  };

  #handleBlockSelect = (event) => {
    const block = event.target;
    if (!(block instanceof HTMLElement) || !this.contains(block)) return;

    const index = this.#slides.indexOf(block);
    if (index >= 0) this.#goTo(index);
  };

  #goTo(index) {
    const count = this.#slides.length;
    if (count < 2) return;

    this.#current = (index + count) % count;
    this.#render();
  }

  #render() {
    const count = this.#slides.length;
    if (count === 0) return;

    for (let index = 0; index < count; index++) {
      const slide = this.#slides[index];
      const background = this.#backgrounds[index];
      const dot = this.#dots[index];
      const position =
        index === this.#current
          ? 'current'
          : index === (this.#current - 1 + count) % count
            ? 'previous'
            : index === (this.#current + 1) % count
              ? 'next'
              : 'hidden';

      slide.dataset.position = position;
      slide.setAttribute('aria-hidden', position === 'current' ? 'false' : 'true');
      slide.querySelector('a')?.setAttribute('tabindex', position === 'current' ? '0' : '-1');
      background?.toggleAttribute('data-active', position === 'current');

      if (dot) {
        if (position === 'current') dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
        dot.setAttribute('aria-label', `Show edition ${index + 1} of ${count}`);
      }
    }
  }

  async #applyImagePalettes() {
    for (let index = 0; index < this.#slides.length; index++) {
      const slide = this.#slides[index];
      const background = this.#backgrounds[index];
      if (!slide || !background) continue;

      const image = slide.querySelector('img.special-editions__image');
      if (!(image instanceof HTMLImageElement)) continue;

      const source = image.currentSrc || image.src;
      if (!source) continue;

      try {
        const palette = await sampleEditionPalette(source);
        if (!palette) continue;

        background.style.setProperty('--edition-color-1', palette.base);
        background.style.setProperty('--edition-color-2', palette.glow);
        background.style.setProperty('--edition-color-3', palette.accent);
      } catch {
      }
    }
  }
}

async function sampleEditionPalette(source) {
  const image = new Image();
  image.crossOrigin = 'anonymous';

  const loaded = new Promise((resolve, reject) => {
    image.addEventListener('load', () => resolve(image), { once: true });
    image.addEventListener('error', () => reject(new Error(`Unable to load image: ${source}`)), {
      once: true,
    });
  });

  image.src = source;
  await loaded;
  if (!image.naturalWidth || !image.naturalHeight) return null;

  const size = 32;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;

  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) return null;

  context.drawImage(image, 0, 0, size, size);
  const imageData = context.getImageData(0, 0, size, size);

  return deriveEditionPalette(imageData.data);
}

/** Below this sampled saturation an image reads as grey, not as a hue. */
const NEUTRAL_SATURATION = 0.06;

function deriveEditionPalette(pixels) {
  let opaqueSamples = 0;
  let sumRed = 0;
  let sumGreen = 0;
  let sumBlue = 0;

  let chromaWeightTotal = 0;
  let hueVectorX = 0;
  let hueVectorY = 0;
  let saturationWeighted = 0;
  let lightnessWeighted = 0;

  let accentScoreBest = -1;
  let accentHue = null;
  let accentSaturation = null;

  for (let offset = 0; offset < pixels.length; offset += 4) {
    const alpha = pixels[offset + 3] ?? 0;
    if (alpha < 125) continue;

    const red = (pixels[offset] ?? 0) / 255;
    const green = (pixels[offset + 1] ?? 0) / 255;
    const blue = (pixels[offset + 2] ?? 0) / 255;

    opaqueSamples += 1;
    sumRed += red;
    sumGreen += green;
    sumBlue += blue;

    const [hue, saturation, lightness] = rgbToHsl(red, green, blue);

    if (saturation >= 0.14 && lightness > 0.08 && lightness < 0.94) {
      const weight = saturation * saturation;
      const angle = (hue * Math.PI) / 180;
      chromaWeightTotal += weight;
      hueVectorX += Math.cos(angle) * weight;
      hueVectorY += Math.sin(angle) * weight;
      saturationWeighted += saturation * weight;
      lightnessWeighted += lightness * weight;
    }

    if (saturation > 0.25 && lightness >= 0.3 && lightness <= 0.85) {
      const score = saturation * (1 - Math.abs(lightness - 0.6));
      if (score > accentScoreBest) {
        accentScoreBest = score;
        accentHue = hue;
        accentSaturation = saturation;
      }
    }
  }

  if (opaqueSamples === 0) return null;

  let dominantHue;
  let dominantSaturation;

  if (chromaWeightTotal > 0.5) {
    dominantHue = ((Math.atan2(hueVectorY, hueVectorX) * 180) / Math.PI + 360) % 360;
    dominantSaturation = clamp(saturationWeighted / chromaWeightTotal, 0, 1);
  } else {
    const [meanHue, meanSaturation] = rgbToHsl(
      sumRed / opaqueSamples,
      sumGreen / opaqueSamples,
      sumBlue / opaqueSamples
    );
    dominantHue = meanHue;
    dominantSaturation = meanSaturation;
  }

  // A near-neutral image (a grey dial with no real chroma anywhere) leaves the
  // dominant hue as rounding noise. Pushing it up to the saturation floors below
  // paints a colour cast the watch doesn't have, so keep the palette grey.
  if (accentHue === null && dominantSaturation < NEUTRAL_SATURATION) {
    return {
      base: formatHsl(0, 0, 0.1),
      glow: formatHsl(0, 0, 0.3),
      accent: formatHsl(0, 0, 0.72),
    };
  }

  const resolvedAccentHue = accentHue ?? (dominantHue + 30) % 360;
  const resolvedAccentSaturation =
    accentSaturation !== null ? clamp(accentSaturation, 0.4, 0.9) : clamp(dominantSaturation + 0.15, 0.3, 0.7);

  return {
    base: formatHsl(dominantHue, clamp(dominantSaturation * 0.85, 0.12, 0.55), 0.1),
    glow: formatHsl(dominantHue, clamp(dominantSaturation * 0.9, 0.18, 0.62), 0.3),
    accent: formatHsl(resolvedAccentHue, resolvedAccentSaturation, 0.72),
  };
}

function rgbToHsl(red, green, blue) {
  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  const delta = max - min;
  const lightness = (max + min) / 2;

  if (delta === 0) return [0, 0, lightness];

  const saturation = delta / (1 - Math.abs(2 * lightness - 1));

  let hue;
  if (max === red) hue = ((green - blue) / delta) % 6;
  else if (max === green) hue = (blue - red) / delta + 2;
  else hue = (red - green) / delta + 4;

  return [((hue * 60) % 360 + 360) % 360, saturation, lightness];
}

function formatHsl(hue, saturation, lightness) {
  return `hsl(${Math.round(hue)} ${Math.round(saturation * 100)}% ${Math.round(lightness * 100)}%)`;
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

if (!customElements.get('special-editions-slider')) {
  customElements.define('special-editions-slider', SpecialEditionsSlider);
}
